import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../config/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  getDocs,
  setDoc,
  deleteField,
  orderBy,
  limit,
} from "firebase/firestore";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./RouteRegistration.css";
import RouteRegistrationView from "./RouteRegistrationView";
import { simplifyPoints, buildCumulativeKm, decodePolylineToPoints, normCityKey } from "../utils/routeRegistration/helpers";

/**
 * RouteRegistration
 * - Cadastro/edição de rotas com polyline (Google Directions)
 * - Correção de preço por trecho (sem duplicar rota) via fareOverrides
 * - Reajuste por KM e Reajuste global (%), escondidos atrás de botões
 * - Taxa de embarque opcional (informada pela empresa)
 * - Solicitação de anúncios (adRequests)
 */

// ===========================

export default function RouteRegistration() {
  const navigate = useNavigate();
  const mapRef = useRef(null);

  const [map, setMap] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  // Form
  const [editingId, setEditingId] = useState(null);
  const [savedRouteId, setSavedRouteId] = useState(null);
  const [addCidades, setAddCidades] = useState([]);
  const [unlockAddCidade, setUnlockAddCidade] = useState(false);
  const [novoPontoNome, setNovoPontoNome] = useState("");
  const [novoPontoUF, setNovoPontoUF] = useState("");
  const [salvandoPonto, setSalvandoPonto] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [outTime, setOutTime] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [price, setPrice] = useState(""); // preço base (sem taxa)
  const [driverPhone, setDriverPhone] = useState("");
  const [originCompanyPhone, setOriginCompanyPhone] = useState("");
  const [destCompanyPhone, setDestCompanyPhone] = useState("");

  // Taxa de embarque (opcional)
  const [hasEmbarkFee, setHasEmbarkFee] = useState(false);
  const [embarkFeeValue, setEmbarkFeeValue] = useState("");

  // Map/route
  const [selectedRouteData, setSelectedRouteData] = useState(null);

  // Reajustes
  const [pricingMode, setPricingMode] = useState("auto");
  const [pricingConfigs, setPricingConfigs] = useState({ valorPorKm: "", taxaFixa: "" });
  const [isCalculating, setIsCalculating] = useState(false);
  const [individualPercent, setIndividualPercent] = useState("");
  const [globalPercent, setGlobalPercent] = useState("");

  // UI toggles (mais didático)
  const [showTrechos, setShowTrechos] = useState(false);
  const [showReajusteKm, setShowReajusteKm] = useState(false);
  const [showReajusteGlobal, setShowReajusteGlobal] = useState(false);

  // Override de preço por trecho (sem duplicar rota)
  const [fareOverrides, setFareOverrides] = useState({});

  const [saving, setSaving] = useState(false);
  // Derivados do trajeto calculado (para preview/edição)
  const distance = selectedRouteData?.distance || "";
  const pathPoints = selectedRouteData?.pathPoints || [];
  const cumKm = selectedRouteData?.cumKm || [];
  const companyFareOverrides = fareOverrides;
  const setDistance = (v) => setSelectedRouteData((p) => ({ ...(p || {}), distance: v }));
  const setPathPoints = (v) => setSelectedRouteData((p) => ({ ...(p || {}), pathPoints: v }));
  const setCumKm = (v) => setSelectedRouteData((p) => ({ ...(p || {}), cumKm: v }));
  const setCompanyFareOverrides = (v) => setFareOverrides(v);

  // ✅ Correções por trecho agora são GLOBAIS da empresa (companies)
  const [companyDocId, setCompanyDocId] = useState(null);
const migrationDoneRef = useRef(false);

  const resolveCompanyDocId = async (uid) => {
    // tenta achar o doc da empresa por userId (caso o id do doc não seja o uid)
    try {
      const q = query(collection(db, "companies"), where("userId", "==", uid), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
    } catch (e) {
      // ignore (pode não ter campo userId nas empresas antigas)
    }
    return uid; // fallback: docId = uid
  };

  const loadCompanyOverrides = async (uid) => {
    try {
      const cid = await resolveCompanyDocId(uid);
      setCompanyDocId(cid);

      const cRef = doc(db, "companies", cid);
      const cSnap = await getDoc(cRef);
      const data = cSnap.exists() ? cSnap.data() : {};
      setFareOverrides(data?.fareOverrides || {});
    } catch (e) {
      console.error("Falha ao carregar overrides da empresa:", e);
      setFareOverrides({});
    } finally {
    }
  };

  const saveCompanyOverrides = async (nextOverrides) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Usuário não autenticado.");
    const cid = companyDocId || (await resolveCompanyDocId(uid));

    const safe = nextOverrides || {};

    // 1) Salva no documento da empresa (fonte de verdade)
    await setDoc(
      doc(db, "companies", cid),
      { fareOverrides: safe, updatedAt: serverTimestamp(), userId: uid },
      { merge: true }
    );

    // 2) Propaga para TODAS as rotas dessa empresa (para o HomeScreen aplicar em qualquer rota que passe pelo trecho)
    //    Fazemos em lotes para evitar limite do writeBatch.
    try {
      const qRoutes = query(collection(db, "routes"), where("userId", "==", uid), limit(1000));
      const snaps = await getDocs(qRoutes);

      const docs = snaps.docs || [];
      const CHUNK = 450; // margem de segurança (limite do batch ~500)
      for (let i = 0; i < docs.length; i += CHUNK) {
        const batch = writeBatch(db);
        docs.slice(i, i + CHUNK).forEach((d) => {
          batch.update(d.ref, { companyFareOverrides: safe, updatedAt: serverTimestamp() });
        });
        await batch.commit();
      }
    } catch (e) {
      console.warn("Falha ao propagar correções para rotas (seguindo só com company doc):", e);
    }

    setCompanyDocId(cid);
  };

  const migrateOverridesFromRoutesOnce = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (migrationDoneRef.current) return;

    migrationDoneRef.current = true;

    try {
      // pega overrides já existentes na empresa
      const cid = companyDocId || (await resolveCompanyDocId(uid));
      const cRef = doc(db, "companies", cid);
      const cSnap = await getDoc(cRef);
      const companyOv = (cSnap.exists() ? cSnap.data()?.fareOverrides : {}) || {};

      // junta overrides antigos de rotas
      const rq = query(collection(db, "routes"), where("userId", "==", uid));
      const rs = await getDocs(rq);

      let merged = { ...companyOv };
      let touched = 0;

      rs.forEach((d) => {
        const r = d.data() || {};
        const ov = r.fareOverrides || {};
        if (ov && typeof ov === "object" && Object.keys(ov).length) {
          merged = { ...merged, ...ov };
          touched += 1;
        }
      });

      // salva no companies
      if (touched > 0) {
        await setDoc(cRef, { fareOverrides: merged, updatedAt: serverTimestamp(), userId: uid }, { merge: true });
        setFareOverrides(merged);

        // opcional: limpa fareOverrides das rotas para não confundir mais
        const batch = writeBatch(db);
        rs.forEach((d) => {
          const r = d.data() || {};
          const ov = r.fareOverrides || {};
          if (ov && typeof ov === "object" && Object.keys(ov).length) {
            batch.update(doc(db, "routes", d.id), { fareOverrides: deleteField(), updatedAt: serverTimestamp() });
          }
        });
        await batch.commit();
      }
    } catch (e) {
      console.error("Migração de overrides falhou:", e);
      // não bloqueia o app
    }
  };

  const [ovCityA, setOvCityA] = useState("");
  const [ovCityB, setOvCityB] = useState("");
  const [ovPrice, setOvPrice] = useState("");

  // Dados
  const [companyName, setCompanyName] = useState("");
  const [myRoutes, setMyRoutes] = useState([]);

  // ===========================
  // ANÚNCIOS (solicitação)
  // ===========================
  const [adModalOpen, setAdModalOpen] = useState(false);
  const [myAdRequests, setMyAdRequests] = useState([]);
  const [myReqFilter, setMyReqFilter] = useState("all");
  const [adPlan, setAdPlan] = useState("destaque"); // destaque | premium_rota
  const [adPlacement, setAdPlacement] = useState("top"); // top | side | mobile | inline
  const [adImageFile, setAdImageFile] = useState(null);
  const [adImagePreview, setAdImagePreview] = useState("");
  const [adLinkDestino, setAdLinkDestino] = useState("");
  const [adDurationDays, setAdDurationDays] = useState(30);
  const [adSubmitting, setAdSubmitting] = useState(false);

  const MAX_AD_IMAGES = 1;
  const MAX_AD_IMAGE_MB = 8;
  const ACCEPTED_AD_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const goToHome = (e) => {
    if (e) e.preventDefault();
    navigate("/");
  };

  const handleSwapRoute = () => {
    if (!origin || !destination) return;
    setOrigin(destination);
    setDestination(origin);
  };

  const verificarSenha = async () => {
    const senha = prompt("Ação protegida! Digite sua senha para confirmar:");
    if (!senha) return false;
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, senha);
      await reauthenticateWithCredential(user, credential);
      return true;
    } catch {
      alert("Senha incorreta!");
      return false;
    }
  };
  // -------------------------------
  // Pontos customizados (trevos/povoados) — GLOBAL
  // Somente EMPRESA cria, mas aparece para todos no app
  // -------------------------------
  const normalizePoint = (s = "") =>
    String(s || "")
      .trim()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const createCustomPoint = async (labelRaw) => {
    try {
      const label = String(labelRaw || "").trim();
      if (label.length < 3) return;

      const norm = normalizePoint(label);

      // já existe no estado? (evita consultas desnecessárias)
      const already = (addCidades || []).some((x) => normalizePoint(x) === norm);
      if (already) return;

      // garante que é empresa logada
      const uid = auth?.currentUser?.uid;
      if (!uid) return;

      // checa se já existe no Firestore
      const qy = query(collection(db, "addCidades"), where("norm", "==", norm), limit(1));
      const snap = await getDocs(qy);
      if (!snap.empty) return;

      await addDoc(collection(db, "addCidades"), {
        label,
        norm,
        createdBy: uid,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Erro ao criar ponto customizado:", e);
    }
  };


  // Reajuste individual (%)
  const aplicarPercentualIndividual = () => {
    const valorAtual = parseFloat(price);
    const perc = parseFloat(individualPercent);
    if (!Number.isFinite(valorAtual) || !Number.isFinite(perc)) {
      alert("Preencha o preço e a porcentagem corretamente.");
      return;
    }
    const novoPreco = valorAtual + valorAtual * (perc / 100);
    setPrice(novoPreco.toFixed(2));
    setPricingMode("manual");
    setIndividualPercent("");
  };

  // Carrega nome da empresa
  
  // ✅ Ouve pontos customizados globais (trevos/povoados)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "addCidades"), orderBy("label", "asc")),
      (snaps) => {
        const items = snaps.docs
          .map((d) => d.data()?.label)
          .filter((v) => typeof v === "string" && v.trim().length > 0);
        setAddCidades(items);
      },
      (err) => {
        console.error("Erro ao ouvir addCidades:", err);
        setAddCidades([]);
      }
    );
    return () => {
      try { unsub(); } catch {}
    };
  }, []);

useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      try {
        const cid = await resolveCompanyDocId(user.uid);
        const r = doc(db, "companies", cid);
        const snap = await getDoc(r);
        if (snap.exists()) setCompanyName(snap.data().companyName || "");

        // ✅ carrega correções globais da empresa + migra overrides antigos das rotas
        await loadCompanyOverrides(user.uid);
        await migrateOverridesFromRoutesOnce();
      } catch (err) {
        console.error(err);
      }
    });
    return () => unsub();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
// Minhas solicitações de anúncio
  useEffect(() => {
    let unsubReq = null;
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (unsubReq) {
        try {
          unsubReq();
        } catch {}
      }
      if (!user) {
        setMyAdRequests([]);
        return;
      }
      const qy = query(collection(db, "adRequests"), where("userId", "==", user.uid));
      unsubReq = onSnapshot(
        qy,
        (sn) => {
          const list = sn.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
              const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
              return tb - ta;
            });
          setMyAdRequests(list);
        },
        (err) => console.error("SNAPSHOT adRequests:", err.code, err.message)
      );
    });

    return () => {
      if (unsubReq) {
        try {
          unsubReq();
        } catch {}
      }
      if (unsubAuth) {
        try {
          unsubAuth();
        } catch {}
      }
    };
  }, []);

  // Inicializa Google Map + DirectionsRenderer
  useEffect(() => {
    const timer = setInterval(() => {
      if (window.google && mapRef.current && !map) {
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: { lat: -10.184, lng: -48.333 },
          zoom: 5,
          disableDefaultUI: true,
          zoomControl: true,
        });

        const renderer = new window.google.maps.DirectionsRenderer({
          map: newMap,
          draggable: true,
          polylineOptions: { strokeColor: "#6366f1", strokeWeight: 5 },
        });

        renderer.addListener("directions_changed", () => {
          const res = renderer.getDirections();
          if (res?.routes?.[0]?.legs?.[0]) {
            const route = res.routes[0];
            const distance = (route.legs[0].distance.value / 1000).toFixed(1);
            const polyline = route.overview_polyline;

            const rawPts = decodePolylineToPoints(polyline);
            const pathPoints = rawPts.length > 1 ? simplifyPoints(rawPts, 3) : [];
            const cumKm = pathPoints.length ? buildCumulativeKm(pathPoints) : [];

            setSelectedRouteData({
              distance,
              summary: route.summary,
              polyline,
              pathPoints,
              cumKm,
            });
          }
        });

        setMap(newMap);
        setDirectionsRenderer(renderer);
        clearInterval(timer);
      }
    }, 500);

    return () => clearInterval(timer);
  }, [map]);

  // Desenha rota quando origem/destino mudam
  useEffect(() => {
    if (!origin || !destination || !directionsRenderer || !map) return;
    if (origin.length <= 3 || destination.length <= 3) return;

    const service = new window.google.maps.DirectionsService();
    service.route(
      { origin, destination, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === "OK") {
          directionsRenderer.setMap(map);
          directionsRenderer.setDirections(result);
        }
      }
    );
  }, [origin, destination, directionsRenderer, map]);

  // CRUD rotas do usuário
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        const qy = query(collection(db, "routes"), where("userId", "==", user.uid));
        const un = onSnapshot(qy, (snap) => {
          setMyRoutes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        return () => {
          try {
            un();
          } catch {}
        };
      } else {
        navigate("/login");
      }
    });
    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [navigate]);

  // ---------------------------
  // Salvar rota (ida e volta)
  // ---------------------------
    const handleSave = async (e) => {
    e.preventDefault();

    const okCity = (s) =>
      /^[^,]{2,}\s*-\s*[A-Za-z]{2}$/i.test(String(s || "").trim());

    if (!auth.currentUser) return alert("Faça login novamente.");

    const o = String(origin || "").trim();
    const d = String(destination || "").trim();

    if (!o || !d) return alert("Preencha origem e destino.");
    if (normCityKey(o) === normCityKey(d)) return alert("Origem e destino não podem ser iguais.");

    // valida formato Cidade-UF (inclui pontos criados manualmente como "Trevo X - TO")
    if (!okCity(o) || !okCity(d)) {
      return alert("Selecione uma cidade válida no formato Cidade - UF.");
    }

    // precisa ter rota desenhada
    if (!selectedRouteData?.pathPoints || !Array.isArray(selectedRouteData.pathPoints) || selectedRouteData.pathPoints.length < 2) {
      return alert("Desenhe a rota no mapa antes de salvar.");
    }

    setSaving(true);

    try {
      const uid = auth.currentUser.uid;

      // distância e geometria
      const distanceKm = Number(selectedRouteData?.distance || 0);
      const pathPoints = Array.isArray(selectedRouteData?.pathPoints) ? selectedRouteData.pathPoints : [];
      const cumKm = Array.isArray(selectedRouteData?.cumKm) && selectedRouteData.cumKm.length === pathPoints.length
        ? selectedRouteData.cumKm
        : buildCumulativeKm(pathPoints);

      // preço base (sem taxa de embarque) — aceita decimais
      const parseMoney = (v) => {
        const n = Number(String(v ?? "").replace(",", "."));
        return Number.isFinite(n) ? n : 0;
      };

      let basePrice = parseMoney(price);

      if (pricingMode === "auto") {
        const vKm = parseMoney(pricingConfigs?.valorPorKm);
        const tFixa = parseMoney(pricingConfigs?.taxaFixa);
        basePrice = Number(((distanceKm || 0) * (vKm || 0) + (tFixa || 0)).toFixed(2));
      }

      if (!(basePrice > 0)) return alert("Informe um preço válido.");

      const feeVal = hasEmbarkFee ? parseMoney(embarkFeeValue) : 0;

      const commonData = {
        userId: uid,
        origin: o,
        destination: d,
        outTime: String(outTime || "").trim(),
        returnTime: String(returnTime || "").trim(),
        price: Number(basePrice.toFixed(2)),
        distance: Number((distanceKm || 0).toFixed(2)),
        pathPoints,
        cumKm,
        hasEmbarkFee: !!hasEmbarkFee,
        embarkFeeValue: Number((feeVal || 0).toFixed(2)),
        driverPhone: String(driverPhone || "").trim(),
        originCompanyPhone: String(originCompanyPhone || "").trim(),
        destCompanyPhone: String(destCompanyPhone || "").trim(),
        updatedAt: serverTimestamp(),
      };

      // dados da rota "volta" (invertida)
      const reversedPoints = [...pathPoints].reverse();
      const reversedCumKm = buildCumulativeKm(reversedPoints);

      const returnData = {
        ...commonData,
        origin: d,
        destination: o,
        outTime: commonData.returnTime,
        returnTime: commonData.outTime,
        pathPoints: reversedPoints,
        cumKm: reversedCumKm,
      };

      const routesRef = collection(db, "routes");

      // helper: acha a rota inversa existente
      const findReverseRoute = () => {
        const oKey = normCityKey(o);
        const dKey = normCityKey(d);
        return (myRoutes || []).find((r) => {
          if (!r?.id) return false;
          if (editingId && r.id === editingId) return false;
          return normCityKey(r.origin) === dKey && normCityKey(r.destination) === oKey;
        });
      };

      if (editingId) {
        // atualiza ida
        await updateDoc(doc(db, "routes", editingId), commonData);

        // atualiza (ou cria) a volta correspondente
        const reverse = findReverseRoute();
        if (reverse?.id) {
          await updateDoc(doc(db, "routes", reverse.id), returnData);
        } else {
          await addDoc(routesRef, { ...returnData, createdAt: serverTimestamp() });
        }

        alert("Rota atualizada (ida e volta)!");
      } else {
        // cria ida
        const idaDoc = await addDoc(routesRef, { ...commonData, createdAt: serverTimestamp() });

        // cria volta (se não existir)
        const reverse = findReverseRoute();
        if (!reverse?.id) {
          await addDoc(routesRef, { ...returnData, createdAt: serverTimestamp(), linkedTo: idaDoc.id });
        }

        alert("Rota salva (ida e volta)!");
      }

      resetForm();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar. Verifique permissões e tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    try {
      if (directionsRenderer) directionsRenderer.setMap(null);
    } catch {}
    setSelectedRouteData(null);
    setOrigin("");
    setDestination("");
    setPrice("");
    setOutTime("");
    setReturnTime("");
    setDriverPhone("");
    setOriginCompanyPhone("");
    setDestCompanyPhone("");
    setHasEmbarkFee(false);
    setEmbarkFeeValue("");
    setEditingId(null);
    setSavedRouteId(null);
    setPricingMode("auto");
    setIndividualPercent("");
    setOvCityA("");
    setOvCityB("");
    setOvPrice("");
  };

  // ---------------------------
  // Anúncios
  // ---------------------------
  const openAdModal = (plan) => {
    if (plan === "premium_rota" && (!origin || !destination)) {
      alert("Preencha Origem e Destino da rota antes de solicitar o Premium por rota.");
      return;
    }
    setAdPlan(plan);
    setAdPlacement(plan === "premium_rota" ? "top" : "side");
    setAdModalOpen(true);
  };

  const submitAdRequest = async () => {
    try {
      if (!auth.currentUser) return alert("Faça login para solicitar anúncio.");
      if (!adImageFile) return alert("Selecione a imagem do banner.");
      if (!adLinkDestino.trim()) return alert("Informe o link de destino (WhatsApp/Instagram/site).");

      const uid = auth.currentUser.uid;
      const safeName = (adImageFile.name || "banner").replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `ad_banners/${uid}/${Date.now()}_${safeName}`;

      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, adImageFile);
      const finalImageUrl = await getDownloadURL(storageRef);

      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + Number(adDurationDays || 30) * 24 * 60 * 60 * 1000
      );

      const payload = {
        userId: uid,
        companyName: (companyName || "").trim() || "Van Particular",
        plan: adPlan,
        placement: adPlacement,
        targetType: adPlan === "premium_rota" ? "route" : "all",
        targetOrigin: adPlan === "premium_rota" ? origin || "" : "",
        targetDestination: adPlan === "premium_rota" ? destination || "" : "",
        bannerImageUrl: finalImageUrl,
        linkDestino: adLinkDestino.trim(),
        status: "pending",
        startsAt: serverTimestamp(),
        expiresAt,
        createdAt: serverTimestamp(),
        storagePath: filePath,
      };

      setAdSubmitting(true);
      await addDoc(collection(db, "adRequests"), payload);

      setAdModalOpen(false);
      setAdImageFile(null);
      setAdImagePreview("");
      setAdLinkDestino("");
      setAdDurationDays(30);
      alert("Solicitação enviada! Vamos aprovar seu anúncio e te avisar.");
    } catch (e) {
      console.error(e);
      alert("Não foi possível enviar a solicitação agora. Tente novamente.");
    } finally {
      setAdSubmitting(false);
    }
  };

  const handleCancelAdRequest = async (requestId) => {
    try {
      if (!auth.currentUser) return;
      const ok = window.confirm("Cancelar esta solicitação?\nEla sairá da fila de aprovação.");
      if (!ok) return;
      await updateDoc(doc(db, "adRequests", requestId), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert("Não foi possível cancelar agora.");
    }
  };

  
  // ===========================
  // CORREÇÃO POR TRECHO (GLOBAL NA EMPRESA)
  // ===========================
  const handleSaveOverride = async () => {
    const a = (ovCityA || "").trim();
    const b = (ovCityB || "").trim();
    const p = Number(String(ovPrice || "").replace(",", "."));

    if (!a || !b) {
      alert("Informe as duas cidades do trecho (A e B).");
      return;
    }
    if (!Number.isFinite(p) || p <= 0) {
      alert("Informe um valor válido para o trecho.");
      return;
    }

    const key = `${normCityKey(a)}|${normCityKey(b)}`;

    const next = { ...(fareOverrides || {}), [key]: Number(p.toFixed(2)) };

    try {
      setFareOverrides(next);
      await saveCompanyOverrides(next);
      setOvCityA("");
      setOvCityB("");
      setOvPrice("");
      alert("Correção salva para TODAS as rotas da empresa!");
    } catch (e) {
      console.error(e);
      alert("Erro ao salvar correção. Verifique sua conexão e tente novamente.");
    }
  };

  const handleRemoveOverride = async (k) => {
    try {
      const next = { ...(fareOverrides || {}) };
      delete next[k];
      setFareOverrides(next);
      await saveCompanyOverrides(next);
    } catch (e) {
      console.error(e);
      alert("Não foi possível remover agora.");
    }
  };


// ---------------------------
  // Render
  // ---------------------------
  const viewProps = {
    adDurationDays,
    adImagePreview,
    adLinkDestino,
    adModalOpen,
    adPlacement,
    adPlan,
    adSubmitting,
    addCidades,
    aplicarPercentualIndividual,
    createCustomPoint,
    destCompanyPhone,
    destination,
    distance,
    driverPhone,
    editingId,
    embarkFeeValue,
    fareOverrides,
    globalPercent,
    goToHome,
    handleCancelAdRequest,
    handleRemoveOverride,
    handleSave,
    handleSaveOverride,
    handleSwapRoute,
    hasEmbarkFee,
    individualPercent,
    isCalculating,
    map,
    mapRef,
    myAdRequests,
    myReqFilter,
    myRoutes,
    navigate,
    normalizePoint,
    novoPontoNome,
    novoPontoUF,
    openAdModal,
    origin,
    originCompanyPhone,
    outTime,
    ovCityA,
    ovCityB,
    ovPrice,
    price,
    pricingConfigs,
    pricingMode,
    returnTime,
    salvandoPonto,
    savedRouteId,
    selectedRouteData,
    setAdDurationDays,
    setAdImageFile,
    setAdImagePreview,
    setAdLinkDestino,
    setAdModalOpen,
    setAdPlacement,
    setAdPlan,
    setAddCidades,
    setDestCompanyPhone,
    setDestination,
    setDriverPhone,
    setEditingId,
    setEmbarkFeeValue,
    setGlobalPercent,
    setHasEmbarkFee,
    setIndividualPercent,
    setIsCalculating,
    setMyReqFilter,
    setNovoPontoNome,
    setNovoPontoUF,
    setOrigin,
    setOriginCompanyPhone,
    setOutTime,
    setOvCityA,
    setOvCityB,
    setOvPrice,
    setPrice,
    setPricingConfigs,
    setPricingMode,
    setReturnTime,
    setSalvandoPonto,
    setShowReajusteGlobal,
    setShowReajusteKm,
    setShowTrechos,
    setUnlockAddCidade,
    showReajusteGlobal,
    showReajusteKm,
    showTrechos,
    submitAdRequest,
    unlockAddCidade,
    verificarSenha
  };

  return <RouteRegistrationView {...viewProps} />;
}
